import { useState, useEffect } from "react"
import { X, Mail, Phone, Building2, Globe, Calendar, User } from "lucide-react"
import { Button } from "components/ui/button"
import { Label } from "components/ui/label"
import { Textarea } from "components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select"
import { Lead, STATUS_OPTIONS, getStatusBadge, formatDateTime } from "utils/leadHelpers"
import { getUsers } from "services/users"

interface User {
  id: number
  username: string
  first_name?: string
  last_name?: string
  email?: string
}

interface LeadDetailPanelProps {
  lead: Lead
  onClose: () => void
  onUpdate: (leadId: number, updates: Partial<Lead>) => Promise<void>
}

export function LeadDetailPanel({ lead, onClose, onUpdate }: LeadDetailPanelProps) {
  const [users, setUsers] = useState<User[]>([])
  const [loadingUsers, setLoadingUsers] = useState(true)
  const [saving, setSaving] = useState(false)

  // Local state for editable fields
  const [status, setStatus] = useState(lead.status)
  const [assignedTo, setAssignedTo] = useState<string>(lead.assigned_to?.toString() || "unassigned")
  const [notes, setNotes] = useState(lead.notes || "")

  // Track if there are unsaved changes
  const hasChanges =
    status !== lead.status ||
    assignedTo !== (lead.assigned_to?.toString() || "unassigned") ||
    notes !== (lead.notes || "")

  // Fetch users for assignment dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true)
        const response = await getUsers()
        const data = Array.isArray(response.data) ? response.data : []
        setUsers(data)
      } catch (err) {
        console.error("Error fetching users:", err)
        setUsers([])
      } finally {
        setLoadingUsers(false)
      }
    }

    fetchUsers()
  }, [])

  // Reset local state when lead changes
  useEffect(() => {
    setStatus(lead.status)
    setAssignedTo(lead.assigned_to?.toString() || "unassigned")
    setNotes(lead.notes || "")
  }, [lead])

  const handleSave = async () => {
    if (!hasChanges) return

    try {
      setSaving(true)
      const updates: Partial<Lead> = {}

      if (status !== lead.status) {
        updates.status = status
      }
      if (assignedTo !== (lead.assigned_to?.toString() || "unassigned")) {
        updates.assigned_to = assignedTo === "unassigned" ? null : parseInt(assignedTo, 10)
      }
      if (notes !== (lead.notes || "")) {
        updates.notes = notes
      }

      await onUpdate(lead.id, updates)
    } catch (err) {
      console.error("Error saving lead:", err)
    } finally {
      setSaving(false)
    }
  }

  const statusBadge = getStatusBadge(lead.status)

  return (
    <div className="flex flex-col min-w-0 min-h-0 h-full" style={{ backgroundColor: "var(--hexa-surface-elevated)" }}>
      {/* Header */}
      <div
        className="flex-shrink-0 px-4 py-3"
        style={{ borderBottom: "1px solid var(--hexa-border)" }}
      >
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold" style={{ color: "var(--hexa-text-primary)" }}>
            Dettaglio Lead
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Chiudi dettaglio lead"
            style={{ color: "var(--hexa-text-secondary)" }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

      </div>

      {/* Content - Scrollable, including long contact names */}
      <div className="flex-1 min-h-0 min-w-0 overflow-y-auto px-4 py-3 space-y-4 scrollbar-hexa">
        <div className="flex flex-wrap items-start gap-2">
          <div className="min-w-0 w-full">
            <h3 className="text-base font-semibold mb-0.5" style={{ color: "var(--hexa-text-primary)" }}>
              {lead.full_name}
            </h3>
            <p className="text-xs" style={{ color: "var(--hexa-text-secondary)" }}>
              {lead.email}
            </p>
          </div>
          <span
            className="inline-flex shrink-0 items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap"
            style={{
              backgroundColor: statusBadge.bgColor,
              color: "var(--hexa-text-primary)",
            }}
          >
            <span
              className="w-1 h-1 rounded-full"
              style={{ backgroundColor: statusBadge.dotColor }}
            />
            {statusBadge.label}
          </span>
        </div>
        {/* Sezione: Informazioni contatto */}
        <div>
          <h4
            className="text-[10px] font-medium uppercase tracking-wide mb-2"
            style={{ color: "var(--hexa-text-secondary)" }}
          >
            Informazioni contatto
          </h4>
          <div
            className="rounded-lg p-3 space-y-2.5"
            style={{ backgroundColor: "var(--hexa-button-cancel)" }}
          >
            <div className="flex items-center gap-2.5">
              <Mail className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--hexa-text-muted)" }} />
              <div>
                <p className="text-[10px]" style={{ color: "var(--hexa-text-muted)" }}>Email</p>
                <p className="text-xs" style={{ color: "var(--hexa-text-primary)" }}>
                  {lead.email}
                </p>
              </div>
            </div>

            {lead.phone && (
              <div className="flex items-center gap-2.5">
                <Phone className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--hexa-text-muted)" }} />
                <div>
                  <p className="text-[10px]" style={{ color: "var(--hexa-text-muted)" }}>Telefono</p>
                  <p className="text-xs" style={{ color: "var(--hexa-text-primary)" }}>
                    {lead.phone}
                  </p>
                </div>
              </div>
            )}

            {lead.company && (
              <div className="flex items-center gap-2.5">
                <Building2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--hexa-text-muted)" }} />
                <div>
                  <p className="text-[10px]" style={{ color: "var(--hexa-text-muted)" }}>Azienda</p>
                  <p className="text-xs" style={{ color: "var(--hexa-text-primary)" }}>
                    {lead.company}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sezione: Gestione */}
        <div>
          <h4
            className="text-[10px] font-medium uppercase tracking-wide mb-2"
            style={{ color: "var(--hexa-text-secondary)" }}
          >
            Gestione
          </h4>
          <div
            className="rounded-lg p-3 space-y-3"
            style={{ backgroundColor: "var(--hexa-button-cancel)" }}
          >
            {/* Status dropdown */}
            <div>
              <Label htmlFor="lead-status" className="text-[10px] mb-1 block" style={{ color: "var(--hexa-text-muted)" }}>
                Status
              </Label>
              <Select value={status} onValueChange={(value) => setStatus(value as Lead["status"])}>
                <SelectTrigger
                  id="lead-status"
                  style={{
                    backgroundColor: "var(--hexa-surface)",
                    borderColor: "var(--hexa-border)",
                    color: "var(--hexa-text-primary)",
                  }}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: option.dotColor }}
                        />
                        {option.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Assigned to dropdown */}
            <div>
              <Label htmlFor="lead-assignee" className="text-[10px] mb-1 block" style={{ color: "var(--hexa-text-muted)" }}>
                Assegnato a
              </Label>
              <Select value={assignedTo} onValueChange={setAssignedTo}>
                <SelectTrigger
                  id="lead-assignee"
                  disabled={loadingUsers}
                  style={{
                    backgroundColor: "var(--hexa-surface)",
                    borderColor: "var(--hexa-border)",
                    color: "var(--hexa-text-primary)",
                  }}
                >
                  <SelectValue placeholder="Seleziona utente" />
                </SelectTrigger>
                <SelectContent className="leads-assignee-options">
                  <SelectItem value="unassigned">Nessuno</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id.toString()}>
                      <div className="flex items-center gap-2">
                        <User className="w-3 h-3" />
                        {user.first_name && user.last_name
                          ? `${user.first_name} ${user.last_name}`
                          : user.username}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Source (read-only) */}
            {lead.source && (
              <div className="flex items-center gap-2.5">
                <Globe className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--hexa-text-muted)" }} />
                <div>
                  <p className="text-[10px]" style={{ color: "var(--hexa-text-muted)" }}>Sorgente</p>
                  <p className="text-xs" style={{ color: "var(--hexa-text-primary)" }}>
                    {lead.source}
                  </p>
                </div>
              </div>
            )}

            {/* Date (read-only) */}
            <div className="flex items-center gap-2.5">
              <Calendar className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--hexa-text-muted)" }} />
              <div>
                <p className="text-[10px]" style={{ color: "var(--hexa-text-muted)" }}>Data ricezione</p>
                <p className="text-xs" style={{ color: "var(--hexa-text-primary)" }}>
                  {formatDateTime(lead.created_at)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sezione: Messaggio */}
        {lead.message && (
          <div>
            <h4
              className="text-[10px] font-medium uppercase tracking-wide mb-2"
              style={{ color: "var(--hexa-text-secondary)" }}
            >
              Messaggio
            </h4>
            <div
              className="rounded-lg p-3"
              style={{ backgroundColor: "var(--hexa-button-cancel)" }}
            >
              <p className="text-xs whitespace-pre-wrap" style={{ color: "var(--hexa-text-primary)" }}>
                {lead.message}
              </p>
            </div>
          </div>
        )}

        {/* Sezione: Note interne */}
        <div>
          <h4
            className="text-[10px] font-medium uppercase tracking-wide mb-2"
            style={{ color: "var(--hexa-text-secondary)" }}
          >
            Note interne
          </h4>
          <Textarea
            aria-label="Note interne"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Aggiungi note interne su questo lead..."
            rows={4}
            style={{
              backgroundColor: "var(--hexa-surface)",
              borderColor: "var(--hexa-border)",
              color: "var(--hexa-text-primary)",
            }}
          />
        </div>
      </div>

      {/* Footer */}
      <div
        className="flex-shrink-0 px-4 py-3"
        style={{ borderTop: "1px solid var(--hexa-border)" }}
      >
        <div className="flex gap-2.5">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
            style={{
              backgroundColor: "var(--hexa-button-cancel)",
              borderColor: "var(--hexa-border)",
              color: "var(--hexa-text-primary)",
            }}
          >
            Chiudi
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="flex-1"
            style={{
              backgroundColor: hasChanges ? "var(--hexa-purple)" : "var(--hexa-border)",
              color: hasChanges ? "white" : "var(--hexa-text-muted)",
            }}
          >
            {saving ? "Salvataggio..." : "Salva modifiche"}
          </Button>
        </div>
      </div>
    </div>
  )
}
